import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  dataLoadFailed,
  dataLoadIdle,
  dataLoadLoading,
  dataLoadReady,
  type DataLoadSnapshot,
} from '../async-load/async-load-state';
import { toDisplayInstant } from '../datetime/ist';
import { Complaint, ComplaintStatus } from '../models/complaint.model';
import { ApiClientService } from './api-client.service';

interface DashboardSummary {
  total: number;
  byStatus: Record<string, number>;
  recent: Complaint[];
}

@Injectable({ providedIn: 'root' })
export class ComplaintService {
  private readonly api = inject(ApiClientService);
  private readonly complaints = signal<Complaint[]>([]);
  private readonly dash = signal<DashboardSummary | null>(null);
  readonly listLoad = signal<DataLoadSnapshot>(dataLoadIdle());
  readonly loading = computed(() => this.listLoad().phase === 'loading');
  /** @deprecated Prefer {@link listLoad} — kept for callers that only read the message string. */
  readonly error = computed(() => this.listLoad().message);

  private readonly detailLoads = signal<Record<string, DataLoadSnapshot>>({});

  detailLoadState(id: string | number): DataLoadSnapshot {
    const key = String(id);
    return this.detailLoads()[key] ?? dataLoadIdle();
  }

  list(): Complaint[] {
    return [...this.complaints()].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }

  getById(id: string | number): Complaint | undefined {
    // Route params are always strings; c.id comes back as a number from the API — compare as strings.
    return this.complaints().find((c) => String(c.id) === String(id));
  }

  summary() {
    const d = this.dash();
    const by = d?.byStatus || {};
    const all = this.complaints();
    const count = (...keys: string[]) =>
      keys.reduce((n, k) => n + (by[k] || all.filter((c) => c.status === k).length), 0);
    return {
      total: d?.total ?? all.length,
      pendingReview: count('submitted', 'pending_verification', 'under_review'),
      activeCases: count('routed', 'scheduled', 'heard', 'reopened'),
      scheduledHearings: count('scheduled'),
      resolved: count('resolved', 'closed'),
    };
  }

  async refresh(params?: { status?: string; q?: string }): Promise<void> {
    this.listLoad.set(dataLoadLoading(this.listLoad()));
    try {
      const [listRes, dashRes] = await Promise.all([
        firstValueFrom(
          this.api.get<{ items: Complaint[]; count: number }>('/grievances', {
            status: params?.status,
            q: params?.q,
          }),
        ),
        firstValueFrom(this.api.get<DashboardSummary>('/grievances/dashboard/summary')),
      ]);
      const items = (listRes.items || []).map((c) => this.normalize(c));
      this.complaints.set(items);
      this.dash.set(dashRes);
      this.listLoad.set(dataLoadReady(items.length > 0));
    } catch (err) {
      this.listLoad.set(dataLoadFailed(err));
    }
  }

  async loadById(id: string | number): Promise<Complaint | undefined> {
    const key = String(id);
    this.detailLoads.update((m) => ({ ...m, [key]: dataLoadLoading(m[key]) }));
    try {
      const item = await firstValueFrom(this.api.get<Complaint>(`/grievances/${id}`));
      const normalized = this.normalize(item);
      this.complaints.update((list) => {
        const rest = list.filter((c) => c.id !== normalized.id);
        return [normalized, ...rest];
      });
      this.detailLoads.update((m) => ({ ...m, [key]: dataLoadReady(true) }));
      return normalized;
    } catch (err) {
      this.detailLoads.update((m) => ({ ...m, [key]: dataLoadFailed(err, 'detail') }));
      return undefined;
    }
  }

  private normalize(c: Complaint): Complaint {
    const meta =
      c.metadataJson && typeof c.metadataJson === 'object'
        ? (c.metadataJson as Record<string, unknown>)
        : {};
    const roleRaw = String(c.assignedRole || meta['assignedRole'] || '').trim();
    const assignedRole = roleRaw && !looksLikeUserId(roleRaw) ? roleRaw : undefined;
    const assignedOfficerName = String(
      c.assignedOfficerName || meta['assignedOfficerName'] || '',
    ).trim() || undefined;
    const investigationDueAt = String(
      c.investigationDueAt || meta['investigationDueAt'] || '',
    ).trim() || null;
    const timelineDaysRaw = c.investigationTimelineDays ?? meta['investigationTimelineDays'];
    const investigationTimelineDays =
      timelineDaysRaw != null && Number.isFinite(Number(timelineDaysRaw))
        ? Number(timelineDaysRaw)
        : null;
    return {
      ...c,
      trackingId: c.trackingId || c.id,
      citizenName: c.citizenName || c.complainantName,
      assignedRole,
      assignedOfficerName,
      category: c.category || '—',
      department: c.department || '—',
      subject: c.subject || c.category || c.complainantName,
      description: c.description || '',
      investigationDueAt,
      investigationTimelineDays,
      status: c.status as ComplaintStatus,
      timeline: (c.timeline || []).map((e) => ({
        ...e,
        at: toDisplayInstant(e.at) || e.at,
      })),
      timelineHistory: (c.timelineHistory || []).map((cycle) => ({
        ...cycle,
        startedAt: toDisplayInstant(cycle.startedAt) || cycle.startedAt,
        endedAt: toDisplayInstant(cycle.endedAt) || cycle.endedAt,
        timeline: (cycle.timeline || []).map((e) => ({
          ...e,
          at: toDisplayInstant(e.at) || e.at,
        })),
      })),
      submittedAt: toDisplayInstant(c.submittedAt) || c.submittedAt,
      updatedAt: toDisplayInstant(c.updatedAt) || c.updatedAt,
      metadataJson: c.metadataJson ?? meta,
    };
  }
}

function looksLikeUserId(value: string): boolean {
  return /^U-OFF-/i.test(value) || /^U-\d/i.test(value);
}
